/**
 * AccountMenu — Phase 1 / Wave 4 closure UX patch
 *
 * Top-right floating avatar + dropdown menu for permanent (non-anon) users.
 * Replaces the original W4.T4 "Account" section that lived at the bottom of
 * My Puzzles, which placed the Sign out button below a list of cards and
 * fired `onSignOut` on a single click — leading to accidental sign-outs that
 * cleared the visible puzzle list (`setCreatedPuzzles([])`) and surfaced as
 * a "puzzles missing after sign-in" bug during W4.T6 human verify.
 *
 * Design choices:
 *   - Floating top-right placement (not BottomNav 5th slot — BottomNav uses
 *     `space-around` over a fixed 4-tab layout; adding a 5th item would
 *     squeeze the existing tabs).
 *   - Hidden entirely for anon users — preserves the W4.T4 contract that
 *     anon sessions never see Account UI.
 *   - Avatar: `user.photoURL` from Google sign-in if present, else first
 *     letter of email in a gradient circle.
 *   - Dropdown anchored under avatar; click-outside / Escape closes.
 *   - Sign out button opens a SignOutConfirmModal — never fires onSignOut
 *     directly. Modal copy clarifies that the local list resets on this
 *     device but cloud puzzles restore on next sign-in.
 *
 * Design follows candy_heart spec (no 1px borders, tonal surfaces, ambient
 * shadow, 16px card radius, theme-vars for colors).
 *
 * Props:
 *   - user (Firebase User | null): current auth user; component renders null
 *     when null or `user.isAnonymous === true`.
 *   - onSignOut (fn): called once after the user confirms sign out in the
 *     modal. The parent owns the actual signOutUser() invocation and any
 *     side-effects (clearing in-memory puzzles, navigating to splash).
 */
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import LanguageSwitcher from "./LanguageSwitcher";

// Phase 2 / W3.T7 — same gradient as App.jsx LOCK_CHIP_GRADIENT (mirrored
// here to avoid importing from App.jsx and creating a circular dep — single
// source of visual truth still lives in DESIGN.md candy_heart spec).
// At a holiday the app root sets the season's chassis vars (--cr-*), so the
// button wears the season; otherwise the fallbacks keep the house gradient.
const UPGRADE_GRADIENT = "linear-gradient(135deg, var(--cr-accent-deep, #a8275a), var(--cr-accent, #ff709f))";

export default function AccountMenu({
  user,
  profile,
  variant = "floating",
  open: openProp,
  onOpenChange,
  onSignIn,
  onSignOut,
  onUpgrade,
  onManageSubscription,
  onDeleteAccount,
  onLegal,
  onRecommend,
  onRate,
}) {
  const { t: tr } = useTranslation();
  const isFooter = variant === "footer";
  // Controlled open (footer variant, owned by AppShell) vs internal (floating).
  const [openState, setOpenState] = useState(false);
  const controlled = typeof openProp === "boolean";
  const open = controlled ? openProp : openState;
  const setOpen = (next) => {
    const v = typeof next === "function" ? next(open) : next;
    if (controlled) onOpenChange && onOpenChange(v);
    else setOpenState(v);
  };
  const [confirming, setConfirming] = useState(false);
  const wrapperRef = useRef(null);

  // Click-outside / Escape closes the dropdown. Skip when the confirm modal
  // is up — the modal owns its own dismiss path so we don't want a backdrop
  // click on the modal to also reach this handler. Footer variant uses its own
  // backdrop for outside-click (the trigger lives in BottomNav, not here), so
  // we only bind the mousedown handler for the floating variant.
  useEffect(() => {
    if (!open || confirming) return undefined;
    const handleMouseDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    if (!isFooter) document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      if (!isFooter) document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, confirming, isFooter]);

  // Anon / pre-auth: no permanent account menu. Footer variant renders nothing
  // (anon sign-in is handled directly by the BottomNav Profile tab). Floating
  // variant offers a voluntary "Sign in" pill when `onSignIn` is wired.
  if (!user || user.isAnonymous) {
    if (isFooter) return null;
    if (typeof onSignIn !== "function") return null;

    const signInWrapperStyle = {
      position: "fixed",
      top: "calc(env(safe-area-inset-top, 8px) + 12px)",
      right: 16,
      zIndex: 200,
    };
    const signInPillStyle = {
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
      height: 40,
      padding: "0 16px",
      border: "none",
      borderRadius: 20,
      cursor: "pointer",
      background: "var(--color-surface)",
      color: "var(--color-primary)",
      fontFamily: "var(--font-display)",
      fontWeight: 800,
      fontSize: 14,
      boxShadow: "0 6px 18px rgba(168,39,90,0.16)",
      transition: "transform 0.15s ease",
    };
    return (
      <div style={signInWrapperStyle}>
        <button
          type="button"
          aria-label={tr("common.account.signIn")}
          onClick={() => onSignIn()}
          style={signInPillStyle}
          data-testid="sign-in-pill"
        >
          <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>👤</span>
          {tr("common.account.signIn")}
        </button>
      </div>
    );
  }

  const initial = (user.email || "?").charAt(0).toUpperCase();
  const photoURL = user.photoURL || null;

  const wrapperStyle = {
    position: "fixed",
    top: "calc(env(safe-area-inset-top, 8px) + 12px)",
    right: 16,
    zIndex: 200,
  };
  const avatarBtnStyle = {
    width: 40,
    height: 40,
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    overflow: "hidden",
    padding: 0,
    background: photoURL
      ? "var(--color-bgAlt)"
      : "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
    color: "var(--color-onPrimary)",
    fontFamily: "var(--font-display)",
    fontWeight: 800,
    fontSize: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 6px 18px rgba(168,39,90,0.18)",
    transition: "transform 0.15s ease",
  };
  const dropdownStyle = {
    position: "absolute",
    top: 48,
    right: 0,
    minWidth: 220,
    maxWidth: 280,
    background: "var(--color-surface)",
    borderRadius: 16,
    padding: 14,
    boxShadow: "0 18px 48px rgba(0,0,0,0.18)",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  };
  const emailStyle = {
    fontFamily: "var(--font-body)",
    fontSize: 13,
    color: "var(--color-textMuted)",
    wordBreak: "break-all",
    lineHeight: 1.4,
    padding: "4px 6px 8px",
  };
  const separatorStyle = {
    height: 0,
    margin: 0,
    background: "var(--color-bgAlt)",
    boxShadow: "0 1px 0 var(--color-bgAlt)",
  };
  const signOutBtnStyle = {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontFamily: "var(--font-body)",
    fontSize: 14,
    fontWeight: 600,
    color: "var(--color-textMuted)",
    textAlign: "left",
    padding: "8px 6px",
    borderRadius: 10,
  };
  // Phase 2 / W3.T7 — Upgrade entry style. Visually distinct from "Sign out"
  // (gradient pill background + white text + bold) so the user reads it as
  // the affirmative CTA inside an otherwise muted dropdown.
  const upgradeBtnStyle = {
    background: UPGRADE_GRADIENT,
    border: "none",
    cursor: "pointer",
    fontFamily: "var(--font-display)",
    fontSize: 14,
    fontWeight: 800,
    color: "#ffffff",
    textAlign: "left",
    padding: "10px 12px",
    borderRadius: 12,
    boxShadow: "0 6px 18px rgba(var(--cr-accent-rgb, 168,39,90), 0.22)",
  };

  // Phase 3 Wave 4 — Manage subscription button style (neutral, mirrors signOutBtnStyle)
  // per PATTERNS.md lines 451-456.
  const manageSubBtnStyle = {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontFamily: "var(--font-body)",
    fontSize: 14,
    fontWeight: 600,
    color: "var(--color-textMuted)",
    textAlign: "left",
    padding: "8px 6px",
    borderRadius: 10,
  };
  // Phase 3 Wave 4 — Delete account button style (destructive, warmer red)
  // per PATTERNS.md lines 457-460.
  const deleteBtnStyle = {
    ...manageSubBtnStyle,
    color: "var(--color-wrong, #c0392b)",
  };

  // Locked decision #9: Upgrade entry visible only for permanent Free users.
  // (Component already returns null for anon/null users above, so the
  // additional gate here only excludes Pro users.)
  const showUpgrade =
    typeof onUpgrade === "function" &&
    !(profile && profile.entitlements && profile.entitlements.pro === true);

  // Manage subscription: only for Pro that comes from a store subscription
  // (#38). Pro given by hand or by RevenueCat has nothing to manage.
  const showManageSub =
    typeof onManageSubscription === "function" &&
    profile &&
    profile.entitlements &&
    profile.entitlements.pro === true &&
    ["appstore", "play"].includes(profile.entitlements.source);
  // #113: l'abbonamento si gestisce nello store in cui è stato fatto. Se è quello
  // di questo telefono il pulsante apre la sua pagina; altrimenti una riga dice dove.
  const storeDelTelefono = { ios: "appstore", android: "play" }[Capacitor.getPlatform()] || null;
  const gestioneQui = showManageSub && profile.entitlements.source === storeDelTelefono;

  // Shared dropdown body — identical content for the floating (top-anchored)
  // and footer (bottom-anchored) variants.
  const menuItems = (
    <>
      {user.email && <div style={emailStyle}>{user.email}</div>}
      <div style={separatorStyle} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "4px 2px 10px" }}>
        <span style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-textMuted)" }}>
          🌐 {tr("common.language.label")}
        </span>
        <LanguageSwitcher tone="menu" showGlobe={false} />
      </div>
      <div style={separatorStyle} />
      {showUpgrade && (
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            if (onUpgrade) onUpgrade();
          }}
          style={upgradeBtnStyle}
          data-testid="account-upgrade-menu-item"
        >
          {tr("common.account.upgrade")}
        </button>
      )}
      {gestioneQui && (
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            if (onManageSubscription) onManageSubscription(profile.entitlements.source);
          }}
          style={manageSubBtnStyle}
          data-testid="manage-subscription-menu-item"
        >
          {tr("common.account.manageSub")}
        </button>
      )}
      {showManageSub && !gestioneQui && (
        <p style={{ ...manageSubBtnStyle, cursor: "default", fontWeight: 500, margin: 0, lineHeight: 1.4 }} data-testid="manage-subscription-elsewhere">
          {tr(profile.entitlements.source === "appstore" ? "common.account.manageOnIphone" : "common.account.manageOnAndroid")}
        </p>
      )}
      {/* #125 e #124: consigliare l'app e lasciarle un voto sullo store. */}
      {typeof onRecommend === "function" && (
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            onRecommend();
          }}
          style={{ ...signOutBtnStyle, color: "var(--color-text)" }}
          data-testid="recommend-menu-item"
        >
          <span aria-hidden="true">💌 </span>{tr("common.account.recommend")}
        </button>
      )}
      {typeof onRate === "function" && (
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            onRate();
          }}
          style={{ ...signOutBtnStyle, color: "var(--color-text)" }}
          data-testid="rate-menu-item"
        >
          <span aria-hidden="true">⭐ </span>{tr("common.account.rate")}
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          setOpen(false);
          setConfirming(true);
        }}
        style={signOutBtnStyle}
        data-testid="sign-out-menu-item"
      >
        {tr("common.account.signOut")}
      </button>
      {typeof onDeleteAccount === "function" && (
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            if (onDeleteAccount) onDeleteAccount();
          }}
          style={deleteBtnStyle}
          data-testid="delete-account-menu-item"
        >
          {tr("common.account.deleteAccount")}
        </button>
      )}
      {/* #121: privacy e termini sempre raggiungibili, non solo dal paywall o mentre si crea. */}
      {typeof onLegal === "function" && (
        <>
          <div style={separatorStyle} />
          <div style={{ display: "flex", gap: 6 }}>
            {["privacy", "terms"].map((pagina) => (
              <button
                key={pagina}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onLegal(pagina);
                }}
                style={{ ...signOutBtnStyle, fontSize: 13, fontWeight: 500 }}
                data-testid={`legal-${pagina}-menu-item`}
              >
                {tr(`common.account.${pagina}`)}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );

  const confirmModal = (
    <SignOutConfirmModal
      open={confirming}
      onCancel={() => setConfirming(false)}
      onConfirm={async () => {
        // Close modal first so async sign-out (auth round-trip) can't
        // re-enter via repeated clicks.
        setConfirming(false);
        if (onSignOut) await onSignOut();
      }}
    />
  );

  // ── Footer variant: sheet anchored just above the BottomNav, opened by the
  // Profile tab. A transparent backdrop closes it on outside tap. No trigger
  // rendered here — that lives in BottomNav. ──
  if (isFooter) {
    const footerBackdropStyle = {
      position: "fixed",
      inset: 0,
      background: "transparent",
      zIndex: 140,
    };
    // The BottomNav is a centered floating bar (left/right:14, margin:auto,
    // max-width 520). The sheet must hang off the Profile tab — the bar's
    // RIGHT edge — not the viewport edge, otherwise on wide screens it drifts
    // far from the centered nav. So mirror the nav's box exactly and right-
    // align the sheet inside it. Wrapper is click-through; only the sheet
    // catches taps.
    const footerSheetWrapperStyle = {
      position: "fixed",
      left: 14,
      right: 14,
      margin: "0 auto",
      maxWidth: 520,
      bottom: "calc(env(safe-area-inset-bottom, 0px) + 92px)",
      zIndex: 150,
      display: "flex",
      justifyContent: "flex-end",
      pointerEvents: "none",
    };
    const footerDropdownStyle = {
      pointerEvents: "auto",
      width: "min(280px, 76vw)",
      transformOrigin: "bottom right",
      background: "var(--color-surface)",
      borderRadius: 18,
      padding: 14,
      boxShadow: "0 22px 56px -12px rgba(122,14,60,0.34), 0 8px 24px -12px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      gap: 10,
      animation: "puzzlyAcctSheet 0.2s cubic-bezier(.34,1.4,.5,1)",
    };
    return (
      <>
        {open && (
          <>
            <div
              aria-hidden="true"
              onClick={() => setOpen(false)}
              style={footerBackdropStyle}
            />
            <div style={footerSheetWrapperStyle}>
              <div role="menu" style={footerDropdownStyle}>
                {menuItems}
              </div>
            </div>
            <style>{`@keyframes puzzlyAcctSheet{from{opacity:0;transform:translateY(10px) scale(.96)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
          </>
        )}
        {confirmModal}
      </>
    );
  }

  // ── Floating variant (default): top-right avatar trigger + dropdown. ──
  return (
    <div ref={wrapperRef} style={wrapperStyle}>
      <button
        type="button"
        aria-label={tr("common.account.menuAria")}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        onClick={() => setOpen((v) => !v)}
        style={avatarBtnStyle}
      >
        {photoURL ? (
          <img
            src={photoURL}
            alt={tr("common.account.avatarAlt")}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <span aria-hidden="true">{initial}</span>
        )}
      </button>

      {open && (
        <div role="menu" style={dropdownStyle}>
          {menuItems}
        </div>
      )}

      {confirmModal}
    </div>
  );
}

/**
 * SignOutConfirmModal — confirm dialog gating the actual sign-out call.
 *
 * Body copy is the locked microcopy from the patch spec — clarifies that
 * cloud puzzles will restore on next sign-in but the local list resets on
 * this device.
 */
function SignOutConfirmModal({ open, onCancel, onConfirm }) {
  const { t: tr } = useTranslation();
  if (!open) return null;

  const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    zIndex: 340,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  };
  const cardStyle = {
    background: "var(--color-surface)",
    borderRadius: "2rem",
    padding: 32,
    maxWidth: 420,
    width: "100%",
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    position: "relative",
  };
  const titleStyle = {
    fontFamily: "var(--font-display)",
    fontSize: 22,
    fontWeight: 800,
    color: "var(--color-text)",
    margin: "0 0 12px",
    textAlign: "center",
  };
  const bodyStyle = {
    fontFamily: "var(--font-body)",
    fontSize: 14,
    color: "var(--color-textMuted)",
    lineHeight: 1.6,
    margin: "0 0 24px",
    textAlign: "center",
  };
  const ctaPrimary = {
    width: "100%",
    padding: "14px 20px",
    border: "none",
    borderRadius: 16,
    fontFamily: "var(--font-body)",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    background:
      "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
    color: "var(--color-onPrimary)",
    boxShadow: "0 8px 24px var(--color-glow)",
  };
  const ctaSecondary = {
    ...ctaPrimary,
    background: "var(--color-bgAlt)",
    color: "var(--color-text)",
    boxShadow: "none",
  };

  return (
    <div onClick={onCancel} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={cardStyle} role="dialog" aria-modal="true">
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 20,
            margin: "0 auto 16px",
            background:
              "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 28,
            boxShadow: "0 8px 24px var(--color-glow)",
          }}
          aria-hidden="true"
        >
          👋
        </div>
        <h2 style={titleStyle}>{tr("common.account.signOutConfirmTitle")}</h2>
        <p style={bodyStyle}>
          {tr("common.account.signOutConfirmBody")
            .split("\n\n")
            .map((para, idx, arr) => (
              <React.Fragment key={idx}>
                {para}
                {idx < arr.length - 1 && (
                  <>
                    <br />
                    <br />
                  </>
                )}
              </React.Fragment>
            ))}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            onClick={onConfirm}
            style={ctaPrimary}
            data-testid="sign-out-confirm-btn"
          >
            {tr("common.account.signOut")}
          </button>
          <button type="button" onClick={onCancel} style={ctaSecondary}>
            {tr("common.account.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
